/// Per-aircraft Pixi container holding a sprite (a stylized arrow), a
/// heading vector projected forward, and a label with callsign /
/// altitude / speed. Containers are positioned in world-space pixels.
///
/// The layer holds a stable map from aircraft id → child container so
/// per-tick `update(aircrafts)` is O(n) and doesn't churn the scene.
/// Click-to-select is wired here too: each aircraft container is
/// `eventMode: 'static'` and emits `aircraft:click` on tap.

import { Container, FederatedPointerEvent, Graphics, Text } from 'pixi.js'
import type { Aircraft } from '../game/Aircraft'
import { typeLabel } from '../game/AircraftDatabase'
import { compactGoal } from '../types/LevelData'
import { radarTheme } from './theme'

export interface AircraftLayerHandlers {
  onSelect(id: string): void
}

interface AircraftNode {
  root: Container
  vector: Graphics
  /// Translucent halo drawn only when the aircraft is selected — sits
  /// between the heading vector and the sprite so the chevron still
  /// reads cleanly on top. Cleared (no draw calls) when unselected,
  /// so the steady-state cost is just one extra Graphics in the tree.
  selectionRing: Graphics
  sprite: Graphics
  label: Text
}

export class AircraftLayer extends Container {
  private nodes = new Map<string, AircraftNode>()
  private handlers: AircraftLayerHandlers

  constructor(handlers: AircraftLayerHandlers) {
    super()
    this.handlers = handlers
  }

  update(aircrafts: Aircraft[]): void {
    const seen = new Set<string>()
    for (const a of aircrafts) {
      seen.add(a.id)
      let node = this.nodes.get(a.id)
      if (!node) {
        node = this.createNode(a.id)
        this.nodes.set(a.id, node)
        this.addChild(node.root)
      }
      this.refresh(node, a)
    }
    // Remove any nodes for aircraft that no longer exist (touched-down
    // aircraft fade out; for MVP we just keep them frozen at threshold).
    for (const [id, node] of this.nodes) {
      if (!seen.has(id)) {
        this.removeChild(node.root)
        node.root.destroy({ children: true })
        this.nodes.delete(id)
      }
    }
  }

  private createNode(id: string): AircraftNode {
    const root = new Container()
    root.eventMode = 'static'
    root.cursor = 'pointer'
    root.hitArea = { contains: (x, y) => Math.hypot(x, y) <= 16 }
    root.on('pointertap', (e: FederatedPointerEvent) => {
      e.stopPropagation()
      this.handlers.onSelect(id)
    })

    const vector = new Graphics()
    root.addChild(vector)
    // Selection ring drawn before the sprite so the chevron sits on
    // top. Started empty; `refresh` populates it only while selected.
    const selectionRing = new Graphics()
    root.addChild(selectionRing)
    const sprite = new Graphics()
    root.addChild(sprite)
    const label = new Text({
      text: '',
      style: {
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 11,
        fill: radarTheme.label,
        lineHeight: 13,
      },
    })
    label.position.set(10, -20)
    root.addChild(label)

    return { root, vector, selectionRing, sprite, label }
  }

  private refresh(node: AircraftNode, a: Aircraft): void {
    node.root.position.set(a.position.x, a.position.y)

    const headingRad = ((a.headingDeg - 90) * Math.PI) / 180

    // Tint priority by severity: crashed > emergency > conflict
    // (traffic) > terrainWarning > selected > default. Each gets its
    // own color + glyph so the player can read the alert at a glance.
    const isCrashed = a.collisionStatus === 'crashed'
    const isEmergency = !isCrashed && a.emergency != null
    const isConflict = !isCrashed && !isEmergency && a.collisionStatus === 'conflict'
    const isTerrain =
      !isCrashed && !isEmergency && !isConflict && a.collisionStatus === 'terrainWarning'
    const tint = isCrashed
      ? 0x801010
      : isEmergency
        ? radarTheme.alert
        : isConflict
          ? radarTheme.conflict
          : isTerrain
            ? radarTheme.terrainWarning
            : a.selected
              ? radarTheme.selected
              : radarTheme.track

    // Heading vector — redrawn each tick (1.5 px stroke; cheap).
    // Crashed aircraft don't draw a vector (they're stationary).
    node.vector.clear()
    if (!isCrashed) {
      node.vector.moveTo(0, 0)
      node.vector.lineTo(Math.cos(headingRad) * 30, Math.sin(headingRad) * 30)
      node.vector.stroke({
        width: 1.5,
        color: tint === radarTheme.track ? radarTheme.vector : tint,
        alpha: 0.9,
      })
    }

    // Selection halo — only drawn when the aircraft is currently
    // selected AND not in a higher-priority alert state (crashed /
    // emergency / conflict / terrain warning already get their own
    // tint and don't need the extra emphasis; doubling up would
    // muddy the read). The ring is a single magenta circle with
    // 35% alpha — outside the contour-band hue family and large
    // enough (16 px radius) to bracket the 6 px chevron with a
    // visible band.
    node.selectionRing.clear()
    if (a.selected && !isCrashed && !isEmergency && !isConflict && !isTerrain) {
      node.selectionRing.circle(0, 0, 16)
      node.selectionRing.fill({ color: radarTheme.selected, alpha: 0.18 })
      node.selectionRing.stroke({
        width: 2,
        color: radarTheme.selected,
        alpha: 0.85,
      })
    }

    node.sprite.clear()
    const r = 6
    node.sprite.moveTo(r, 0)
    node.sprite.lineTo(-r * 0.7, -r * 0.7)
    node.sprite.lineTo(-r * 0.4, 0)
    node.sprite.lineTo(-r * 0.7, r * 0.7)
    node.sprite.closePath()
    node.sprite.fill({ color: tint, alpha: 1 })
    node.sprite.rotation = headingRad

    const tag = isCrashed
      ? ' ✕'
      : isEmergency
        ? ' ⚠'
        : isConflict
          ? ' ▲'
          : isTerrain
            ? ' ⛰'
            : ''
    const goal = compactGoal(a.goal)
    // Five-line data block: callsign / type / altitude+speed / goal /
    // squawk. Squawk on its own row so the row stays scannable; "Sq"
    // prefix avoids confusion with the FL / speed pair above.
    const lines: string[] = [`${a.flightNumber}${tag}`]
    lines.push(typeLabel(a.type))
    lines.push(`FL${formatAltitude(a.altitudeFt)} ${Math.round(a.speedKts)}kt`)
    if (goal) lines.push(goal)
    lines.push(`Sq ${a.squawk}`)
    node.label.text = lines.join('\n')
    node.label.style.fill = isCrashed
      ? 0xff8080
      : tint === radarTheme.track
        ? a.selected
          ? radarTheme.selected
          : radarTheme.label
        : tint
  }
}

function formatAltitude(ft: number): string {
  return Math.round(ft / 100)
    .toString()
    .padStart(3, '0')
}
